import React from 'react';
import {
  CheckoutUserResponseData,
  FormikRadioInput,
  FormikInput as Input,
  FormikSelect as Select,
  getExpiringLocalStorage,
  MakePaymentArguments,
  SaleProductPrice,
  SaleProductVariant,
  SavePaymentArguments,
  useAppContext,
  useToast,
  useTranslator,
  PaymentMethod,
  ShippingOptionsResponseData,
} from '@front-activerse/common-components';
import { Form, Formik } from 'formik';
import SummaryContainer from './SummaryContainer';
import { notAvaiblePaymentMethods } from '../const';
import { useErrorBoundary } from 'react-error-boundary';
import { Row, Col, Button } from 'reactstrap';
import validationSchema from './validationSchema';
import { useNavigate } from 'react-router-dom';
import { sha256 } from 'js-sha256';

interface ICheckoutForm {
  pathThankYou: string;
  pathPaymentMethods: string;
  shoppingCartToken: string;
  makePayment: (args: MakePaymentArguments) => Promise<any>;
  product: {
    saleProductVariant: SaleProductVariant;
    price: SaleProductPrice;
  };
  userData: CheckoutUserResponseData;
  productPrice: SaleProductPrice;
  paymentMethods: PaymentMethod[];
  savePayment: (args: SavePaymentArguments) => Promise<any>;
  shippingOptions: ShippingOptionsResponseData;
  disableCodOption?: boolean;
}

const CheckoutForm: React.FC<ICheckoutForm> = ({
  pathThankYou,
  pathPaymentMethods,
  shoppingCartToken,
  makePayment,
  product,
  userData,
  productPrice,
  savePayment,
  paymentMethods,
  shippingOptions,
  disableCodOption,
}) => {
  const { t } = useTranslator();
  const {
    consts: { DISABLE_COD_FOR_PRODUCTS_ARRAY },
  } = useAppContext();
  const { showBoundary } = useErrorBoundary();
  const { toastError } = useToast();
  const navigate = useNavigate();

  const initialValues = {
    saleMethod: `${paymentMethods[0].methodId}_${paymentMethods[0].channelId}`,
    name: userData.shipping?.name || '',
    address: userData.shipping?.street || '',
    postalCode: userData.shipping?.postalCode || '',
    city: userData.shipping?.city || '',
    phone: userData.shipping?.phone || '',
    countryCode: 'PL',
  };

  const avaibleShippingOptions = shippingOptions?.filter(
    (option) => option.onlyVirtualProducts === product.saleProductVariant.saleProduct.isVirtual
  );

  const getDeliveryOption = (values: typeof initialValues) => {
    const { saleMethod } = values;
    const shippingOption =
      saleMethod === 'cod'
        ? avaibleShippingOptions.find((option) => option.isCod)
        : avaibleShippingOptions.find((option) => !option.isCod);
    return shippingOption;
  };

  const getDeliveryOptionPrice = (values: typeof initialValues) => {
    const deliveryOption = getDeliveryOption(values);

    if (deliveryOption === undefined) {
      const err = new Error('no delivery option avaible');
      showBoundary(err);
      throw err;
    }

    let price = deliveryOption?.price;
    if (deliveryOption?.freeFromCartValue) {
      if (product.price.amount >= deliveryOption.freeFromCartValue.amount) {
        price = {
          amount: 0,
          currency: product.price.currency,
          __typename: deliveryOption.price.__typename,
        };
      }
    }

    return price;
  };

  const disabledCodOption = () => {
    return (
      disableCodOption ||
      (Array.isArray(DISABLE_COD_FOR_PRODUCTS_ARRAY) &&
        DISABLE_COD_FOR_PRODUCTS_ARRAY.map(Number).includes(product.saleProductVariant.id))
    );
  };

  const getCodOption = () => {
    return avaibleShippingOptions?.find((option) => option.isCod);
  };

  const totalPrice = (values: typeof initialValues) => {
    return {
      amount: product.price.amount + getDeliveryOptionPrice(values).amount,
      currency: product.price.currency,
    };
  };

  const handleOnSubmit = async (values: typeof initialValues) => {
    if (values.saleMethod === 'cod' || !product.saleProductVariant.saleProduct.isVirtual) {
      const deliveryAddress = {
        name: values.name,
        address: values.address,
        postalCode: values.postalCode,
        city: values.city,
        phone: values.phone,
        countryCode: values.countryCode,
      };
      try {
        const responseSavePayment = await savePayment({
          productId: product.saleProductVariant.id,
          source: getExpiringLocalStorage('saleSource'),
          promoCode: getExpiringLocalStorage('promoCode'),
          saleShoppingId: getDeliveryOption(values)?.id,
          shoppingCartToken,
          email: userData.email,
          deliveryAddress,
        });

        if (responseSavePayment.data.sale.savePayment.__typename !== 'Payment') {
          toastError();
        }

        const responseMakePayment = await makePayment({
          token: shoppingCartToken,
        });

        if (responseMakePayment.data.sale.makePayment.__typename !== 'Payment') {
          toastError(`${t('error/message/generic')}. ${t('try-again')}.`);
          window.location.reload();
        } else {
          // redirect to thankyou page
          navigate(pathThankYou.replace(':token', shoppingCartToken));
        }
      } catch (err) {
        showBoundary(err);
      }
    } else {
      const [methodId, channelId] = values.saleMethod.split('_');
      const cancelUrl = `${document.location.protocol}//${
        document.location.host
      }${pathPaymentMethods.replace(':token', shoppingCartToken)}`;
      const successUrl = `${document.location.protocol}//${
        document.location.host
      }${pathThankYou.replace(':token', shoppingCartToken)}`;

      try {
        const responseSavePayment = await savePayment({
          shoppingCartToken: shoppingCartToken,
          source: getExpiringLocalStorage('saleSource'),
          productId: product.saleProductVariant.id,
          saleShoppingId: getDeliveryOption(values)?.id,
          promoCode: getExpiringLocalStorage('promoCode'),
          email: userData.email,
        });

        if (responseSavePayment.data.sale.savePayment.__typename !== 'Payment') {
          toastError();
        }

        const responseMakePayment = await makePayment({
          token: shoppingCartToken,
          methodId: +methodId,
          channelId: channelId === 'null' ? null : channelId,
          successUrl: successUrl,
          cancelUrl: cancelUrl,
        });

        if (responseMakePayment.data.sale.makePayment.__typename !== 'Payment') {
          toastError(`${t('error/message/generic')}. ${t('try-again')}.`);
          window.location.reload();
        } else {
          global.location.href = responseMakePayment.data.sale.makePayment.url;
        }
      } catch (err) {
        showBoundary(err);
      }
    }
  };

  const renderPaymentMethods = () => {
    return (
      <>
        {paymentMethods.map((method) => {
          const value = `${method.methodId}_${method.channelId}`;
          if (method.__typename === 'SaleMethod' && !notAvaiblePaymentMethods.includes(value)) {
            const isMacLike = /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent);
            const isChrome =
              /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.userAgent);
            const isSafari =
              /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
            const isMobileChrome = isChrome && /Mobile/.test(navigator.userAgent);

            // Apple Pay filtering: show only on Apple devices in Safari or mobile Chrome
            if (method.isApplePay) {
              const shouldShowApplePay = isMacLike && (isSafari || isMobileChrome);
              if (!shouldShowApplePay) {
                return null;
              }
            }
            return (
              <FormikRadioInput key={value} name="saleMethod" formBox={false} value={value}>
                <div>
                  <div className="d-inline-block me-2">{method.name}</div>

                  <div className="d-inline-block" style={{ height: '21px', minWidth: '50px' }}>
                    <img style={{ maxHeight: '21px', width: 'auto' }} src={method.logoUrl} alt="" />
                  </div>

                  {value === '1_blikbnpl' && method.description && (
                    <div
                      className="payment-method-description"
                      dangerouslySetInnerHTML={{ __html: method.description }}
                    />
                  )}
                </div>
              </FormikRadioInput>
            );
          }
        })}

        {!disabledCodOption() && getCodOption() && (
          <FormikRadioInput key="cod" name="saleMethod" formBox={false} value="cod">
            {getCodOption()?.name}
          </FormikRadioInput>
        )}
      </>
    );
  };

  const isPast48Hours = (created: string) => {
    const now = new Date();
    const userCreatedDate = new Date(created);
    const timeDifference = +now - +userCreatedDate;

    const hoursDifference = timeDifference / (1000 * 60 * 60);
    return hoursDifference >= 48;
  };

  const sendBeginCheckoutEvent = () => {
    const userEmail = global.localStorage.getItem('userEmail') || '';
    const userCreated = global.localStorage.getItem('userCreated') || '';
    const dataLayerObject = {
      event: 'begin_checkout',
      user_email: userEmail,
      email: userEmail,
      sha256_email: sha256(userEmail),
      new_customer: !isPast48Hours(userCreated),
      ecommerce: {
        currency: productPrice.currency,
        value: productPrice.amount,
        items: [
          {
            item_id: product.saleProductVariant.id,
            item_name: product.saleProductVariant.saleProduct.name,
            price: productPrice.amount,
            quantity: 1,
          },
        ],
      },
    };

    if (window) {
      window.dataLayer.push(dataLayerObject);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      validationSchema={validationSchema(product.saleProductVariant.saleProduct.isVirtual)}
    >
      {({ values, isValid, isSubmitting }) => {
        return (
          <Form id="1">
            <Row>
              <Col xs={{ size: 12, order: 2 }} md={{ size: 6, order: 1 }}>
                <div className="page-container">
                  <div className="payment-container">
                    <h3 className="title-medium semi-bold mb-xl">
                      {t('paymentMethods.form.title')}
                    </h3>
                    {renderPaymentMethods()}

                    {(!product.saleProductVariant.saleProduct.isVirtual ||
                      values.saleMethod === 'cod') && (
                      <>
                        <h4 className="title-medium semi-bold mb-xl mt-xl">
                          {t('paymentMethods.shippingAddress.title')}
                        </h4>
                        <Input
                          type="text"
                          name="name"
                          label={t('paymentMethods.shippingAddress.name')}
                          translateErrMsg
                        />
                        <Input
                          type="text"
                          name="address"
                          label={t('paymentMethods.shippingAddress.address')}
                          translateErrMsg
                        />
                        <Row>
                          <Col>
                            <Input
                              type="text"
                              name="postalCode"
                              label={t('paymentMethods.shippingAddress.postalCode')}
                              translateErrMsg
                            />
                          </Col>
                          <Col>
                            <Input
                              type="text"
                              name="city"
                              label={t('paymentMethods.shippingAddress.city')}
                              translateErrMsg
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <Input
                              type="tel"
                              name="phone"
                              label={t('paymentMethods.shippingAddress.phone')}
                              translateErrMsg
                            />
                          </Col>
                          <Col>
                            <Select
                              name="countryCode"
                              label={t('paymentMethods.shippingAddress.country')}
                              inline={false}
                              translateErrMsg
                            >
                              <option value="PL">
                                {t('paymentMethods.shippingAddress.country.poland')}
                              </option>
                            </Select>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>
                </div>
              </Col>

              <Col xs={{ size: 12, order: 1 }} md={{ size: 6, order: 2 }}>
                <div className="page-container mb-xl">
                  <div className="summary-container">
                    <SummaryContainer
                      values={values}
                      product={product}
                      deliveryOptionPrice={getDeliveryOptionPrice(values)}
                      totalPrice={totalPrice(values)}
                      productPrice={productPrice}
                      disableCodOption={disableCodOption}
                    />

                    <Button
                      type="submit"
                      color="primary"
                      className="d-none d-md-block w-100 mt-xl"
                      disabled={!isValid || isSubmitting}
                      onClick={sendBeginCheckoutEvent}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          <span>{t('paymentMethods.makePayment.button')}</span>
                        </>
                      ) : (
                        t('paymentMethods.makePayment.button')
                      )}
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>

            <div className="mx-l mt-xl d-md-none">
              <Button
                type="submit"
                color="primary"
                className="w-100"
                disabled={!isValid || isSubmitting}
                onClick={sendBeginCheckoutEvent}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    <span>{t('paymentMethods.makePayment.button')}</span>
                  </>
                ) : (
                  t('paymentMethods.makePayment.button')
                )}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CheckoutForm;
